import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import { MedicalCaseData, TaskStatus, UserData } from 'chaika-frontend-types/frontend-cms';

import { isDefined } from '@utils/isDefined';

import type { TaskFormState } from '../types';

interface Deps {
    getUser(id?: string): UserData | undefined;
    medicalCases: MedicalCaseData[];
    authUserPracticeIds: string[];
}

export interface TaskPermissions {
    isCreationMode: boolean;
    isPatientSelectDisabled: boolean;
    userCanChangeStatus: boolean;
    userCanEditComment: boolean;
    canEdit: boolean;
    canEditDate: boolean;
    canCreate: boolean;
    isCancelled: boolean;
    isExecuted: boolean;
    author?: UserData;
    executors: UserData[];
    actualExecutor?: UserData;
}

// Порт computed-геттеров TaskStore 1:1. Права на медзадачу (PHI) — покрыто taskPermissions.unit.ts.
export function computeTaskPermissions(state: TaskFormState, deps: Deps): TaskPermissions {
    const { getUser, medicalCases, authUserPracticeIds } = deps;
    const {
        authUserId,
        authorId,
        requesterId,
        executorIds,
        practiceIds,
        actualExecutorId,
        status,
        type,
        taskId,
        entryId,
        medicalCaseIds,
        authorsAssistantId,
        requestersAssistantId,
        executorsAssistantId,
    } = state;

    const author = getUser(authorId);
    const requester = getUser(requesterId);
    const actualExecutor = getUser(actualExecutorId);
    const executors = compact(executorIds.map(getUser));

    const isCancelled = status === TaskStatus.Cancelled;
    const isExecuted = status === TaskStatus.Completed;

    const isAuthorsAssistant = isDefined(authorsAssistantId) && authUserId === authorsAssistantId;
    const isRequestersAssistant = isDefined(requestersAssistantId) && authUserId === requestersAssistantId;
    const isExecutorsAssistant =
        isDefined(executorsAssistantId) && !!executorsAssistantId?.some(id => authUserId === id);

    const isEpisodeManager = isDefined(authUserId)
        ? medicalCaseIds.map(id => medicalCases.find(it => it.id === id)).some(it => it?.managerId === authUserId)
        : false;

    const isTaskPracticeMember = practiceIds.some(id => authUserPracticeIds.includes(id));

    const userCanChangeStatus =
        (!isEmpty(executorIds) && executorIds.some(id => authUserId === id)) ||
        isTaskPracticeMember ||
        isExecutorsAssistant ||
        actualExecutorId === authUserId;

    const isAuthorOrActualExecutor = authorId === authUserId || actualExecutorId === authUserId;
    const userCanEditComment =
        isTaskPracticeMember ||
        (!isEmpty(executorIds)
            ? isAuthorOrActualExecutor || executorIds.some(id => authUserId === id)
            : isAuthorOrActualExecutor);

    const isUserAuthor = isDefined(author?.id) ? author.id === authUserId : true;
    const isUserRequester = isDefined(requester?.id) ? requester.id === authUserId : true;
    const userCanEdit =
        isUserAuthor || isUserRequester || isAuthorsAssistant || isRequestersAssistant || isEpisodeManager;
    const userCanEditDate = userCanEdit || isExecutorsAssistant || userCanEditComment;

    return {
        isCreationMode: isUndefined(taskId),
        isPatientSelectDisabled: isDefined(entryId),
        userCanChangeStatus,
        userCanEditComment,
        canEdit: userCanEdit && !isCancelled && !isExecuted,
        canEditDate: userCanEditDate && !isCancelled && !isExecuted,
        canCreate: [requester?.id, type].every(it => !isEmpty(it)) && (!isEmpty(executorIds) || !isEmpty(practiceIds)),
        isCancelled,
        isExecuted,
        author,
        executors,
        actualExecutor,
    };
}
